import type { ConstructionTypeData, ProjectMethodologyEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { Button, ContextMenu } from 'uikit';

import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CreatingTypes } from './commonData';
import { CreateOtherModal } from './CreateOtherModal';

import Styles from './CreateOtherModal.module.css';

interface ICreateOther {
    currentConstructionTypeId?: number;
    reloadHandler: (id: number) => void;
    methodology: ProjectMethodologyEnum;
    constructionTypes?: ConstructionTypeData[];
    workOrderId?: number;
    workOrderChangeId?: number;
    isDisabled?: boolean;
    workOrderObjectId?: number;
    workOrderStatus?: WorkOrderStatusEnum;
    accessRules?: IUseAccess['workOrderChangeUer'];
}

export const CreateOther: React.FC<ICreateOther> = (props) => {
    const {
        currentConstructionTypeId,
        reloadHandler,
        constructionTypes,
        methodology,
        workOrderId,
        isDisabled = false,
        workOrderObjectId,
        workOrderStatus,
        workOrderChangeId,
        accessRules,
    } = props;

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const { contractId } = projectData || {};
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const ref = useRef(null);
    const [activeModal, setActiveModal] = useState<CreatingTypes | null>(null);
    const [isContextMenuOpen, setIsContextMenuOpen] = useState<boolean>(false);

    const closeCreateModal = useCallback(() => {
        setActiveModal(null);
    }, []);

    const handleContextToggle = useCallback(() => {
        setIsContextMenuOpen((prev) => {
            if (!prev) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_create other create button_pressed',
                    projectId,
                    orderId: Number(workOrderId),
                    orderStatus: workOrderStatus,
                    projectName: projectData?.name,
                    projectType: projectData?.contractType,
                    projectDO: projectData?.subsidiary?.name,
                    methodology: projectData?.methodology,
                });
            }

            return !prev;
        });
    }, [
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        workOrderId,
        workOrderStatus,
    ]);

    const isAccessRulesEnabled = Boolean(accessRules);

    const isDisabledManually = isAccessRulesEnabled && !accessRules?.canManualCreation;
    const isDisabledFromDirectory = isAccessRulesEnabled && !accessRules?.canAddingEntries;

    const actions = useMemo(
        () => [
            {
                label: 'Вручную',
                action: () => {
                    setActiveModal(CreatingTypes.MANUAL);
                    handleContextToggle();
                },
                disabled: isDisabledManually,
            },
            {
                label: 'Из справочника методик расчета',
                action: () => {
                    setActiveModal(CreatingTypes.BOOK);
                    handleContextToggle();
                },
                disabled: isDisabledFromDirectory,
            },
        ],
        [handleContextToggle, isDisabledFromDirectory, isDisabledManually],
    );

    return (
        <>
            <Button
                view="secondary"
                size="s"
                onClick={handleContextToggle}
                ref={ref}
                label="Создать прочие"
                disabled={isDisabled}
                className={Styles.leftPanelWidth}
            />
            <ContextMenu
                isOpen={isContextMenuOpen}
                anchorRef={ref}
                getItemLabel={({ label }) => label}
                items={actions}
                onClickOutside={handleContextToggle}
                direction="downStartLeft"
                getItemOnClick={({ action }) => action}
                className={Styles.leftPanelWidth}
            />
            {activeModal && (
                <CreateOtherModal
                    handleClose={closeCreateModal}
                    creatingType={activeModal}
                    currentConstructionTypeId={currentConstructionTypeId}
                    reloadHandler={reloadHandler}
                    constructionTypes={constructionTypes}
                    methodology={methodology}
                    workOrderId={workOrderId}
                    workOrderObjectId={workOrderObjectId}
                    workOrderStatus={workOrderStatus}
                    workOrderChangeId={workOrderChangeId}
                    contractId={contractId}
                />
            )}
        </>
    );
};

CreateOther.displayName = 'CreateOther';
